import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useDebouncedCallback } from "use-debounce";
import { useOrganizationVehicleAlertsQuery } from "../../api/network/adminApiServices";
import {
  setListingQueryParams,
  TListingQueryParams,
} from "../../api/store/commonSlice";
import { TLegacyAlert } from "../../api/types/Alert";
import { TSelectboxOption } from "../../components/admin/formFields";
import HeaderView from "../../components/admin/headerView";
import AdminTable from "../../components/adminTable";
import ListingTableHeader from "../../components/listingTableHeader";
import {
  EditListingColumnsButton,
  ModalEditListingColumns,
  TListingColumn,
} from "../../components/modalEditListingColumns";
import Pagination, { TPaginationSelected } from "../../components/pagination";
import { TAdminTableRowData } from "../../components/types";
import { APP_CONFIG } from "../../constants/constants";
import { routeUrls } from "../../navigation/routeUrls";
import { SortingStatusWithReset } from "../../utils/adminListings";
import { dbTimeToJsTime } from "../../utils/dateTime";
import {
  localStorageKeys,
  useLocalStorage,
} from "../../utils/localStorageItems";
import { useLoggedInUserData } from "../../utils/user";

const columnOrderingFields: Record<string, string> = {
  "Asset Id": "deviceid",
  "Asset Description": "ft_description",
  "Display Name": "displayname",
  Driver: "drivername",
  Description: "rule.description",
  "Last Alert": "timestamp",
  "Alert Rule": "ruleid",
  "Alert Status": "alert_status",
};

const ScreenVehicleAlerts = () => {
  const thisOrgDriverAllowed = useLoggedInUserData("hasdrivers");
  const showDriver = thisOrgDriverAllowed === "1";

  const all_columns = [
    // "Sr No.",
    "Asset Id",
    "Asset Description",
    "Display Name",
    ...(showDriver ? ["Driver"] : []),
    "Description",
    "Last Alert",
    "Alert Rule",
    "Alert Status",
  ];

  const { t: tMain } = useTranslation();
  const { t } = useTranslation("translation", { keyPrefix: "vehicleAlerts" });
  const listingQueryParams: TListingQueryParams = useSelector(
    (state: any) => state.commonReducer.listingQueryParams
  );
  const { vehicleAlerts: orgVehicleAlertsQueryParams } = listingQueryParams;
  const dispatch = useDispatch();
  const listingWrapperRef = React.useRef<HTMLDivElement>(null);

  // columns
  const { getLocalStorageItem } = useLocalStorage();
  const savedColumns = getLocalStorageItem(
    localStorageKeys.columns.VehicleAlerts
  );
  const [columns, setColumns] = useState<TListingColumn[]>(
    all_columns.map((colItem, _) => {
      return {
        name: colItem,
        show: !!savedColumns ? savedColumns.includes(colItem) : true,
      };
    })
  );

  const debouncedSetSearchKeyword = useDebouncedCallback((value: string) => {
    dispatch(
      setListingQueryParams({
        ...listingQueryParams,
        vehicleAlerts: {
          ...orgVehicleAlertsQueryParams,
          page: 1,
          search: value,
        },
      })
    );
  }, 500);

  const handleOrderingChange = (ordering?: string) => {
    dispatch(
      setListingQueryParams({
        ...listingQueryParams,
        vehicleAlerts: {
          ...orgVehicleAlertsQueryParams,
          ordering,
          page: 1,
        },
      })
    );
  };

  // fetching vehicle alert data
  const {
    data: dataOrgVehicleAlerts,
    isFetching: isFetchingOrgVehicleAlerts,
    error,
  } = useOrganizationVehicleAlertsQuery({
    ...orgVehicleAlertsQueryParams,
    // 'search_fields' intentionally not added here as default search fields are defined in the API service
  });
  const { count, next, previous, results } = dataOrgVehicleAlerts || {};

  // smooth scroll listing to the top on page change
  useEffect(() => {
    listingWrapperRef?.current?.scrollTo({ behavior: "smooth", top: 0 });
  }, [dataOrgVehicleAlerts]);

  const tableData: TAdminTableRowData[] = !!results
    ? (results || ([] as TLegacyAlert[])).map(
        (item: TLegacyAlert, index: number) => ({
          navLink: `${routeUrls.dashboardChildren.vehicle_alert}/${item.deviceid}`,
          cellData: [
            //   index + 1 ?? '-', // "Sr No",
            item?.deviceid ?? "-", // "device id",
            item?.ft_description ?? "-",
            item?.displayname ?? "-",
            ...(showDriver ? [item?.drivername ?? "-"] : []),
            item?.rule?.description ?? "-", // "Description",
            new Date(dbTimeToJsTime(item?.timestamp)).toLocaleString() ?? "-", // "Last Alert"
            item?.rule?.ruleid ?? "-", // "Alert Rule",
            <span className="text-field-success">{tMain("yes")}</span>, // "Alert Status",
          ].filter((_, index) => columns[index].show),
        })
      )
    : [];

  return (
    <>
      <HeaderView title={t("heading")} />
      <div className={`${APP_CONFIG.DES.DASH.P_HORIZ} pt-4`}>
        <ListingTableHeader
          heading={
            <div className="flex items-center gap-4">
              {/* Heading */}
              <span>{t("listing_heading")}</span>

              {/* Sorted by label */}
              {!!orgVehicleAlertsQueryParams?.ordering && (
                <SortingStatusWithReset
                  ordering={orgVehicleAlertsQueryParams.ordering}
                  columnOrderingFields={columnOrderingFields}
                  onResetOrdering={() => handleOrderingChange(undefined)}
                />
              )}
            </div>
          }
          searchBoxPlaceholder={t("search_placeholder")}
          searchBoxValue={orgVehicleAlertsQueryParams.search}
          searchBoxOnChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            debouncedSetSearchKeyword(e.target.value)
          }
          searchBoxOnClear={() => debouncedSetSearchKeyword("")}
        />
        <ModalEditListingColumns
          columns={columns}
          setColumns={setColumns}
          lsKey={localStorageKeys.columns.VehicleAlerts}
        />
        <div className="py-4 mt-4 relative">
          <EditListingColumnsButton disabled={isFetchingOrgVehicleAlerts} />
          <AdminTable
            viewportHeightOffset={"400"}
            columns={columns
              .filter((item) => item.show)
              .map((item) => item.name)}
            data={tableData}
            isLoading={isFetchingOrgVehicleAlerts}
            listingQueryParams={orgVehicleAlertsQueryParams}
            listingWrapperRef={listingWrapperRef}
            onOrderingChange={handleOrderingChange}
            ordering={orgVehicleAlertsQueryParams.ordering}
            sortableColumns={columnOrderingFields}
          />
          {!isFetchingOrgVehicleAlerts && (
            <Pagination
              pageSize={orgVehicleAlertsQueryParams.page_size}
              handlePageSizeChange={(e: TSelectboxOption | null) => {
                dispatch(
                  setListingQueryParams({
                    ...listingQueryParams,
                    vehicleAlerts: {
                      ...orgVehicleAlertsQueryParams,
                      page: 1,
                      page_size: parseInt(`${e?.value}`),
                    },
                  })
                );
              }}
              totalPages={
                count
                  ? Math.ceil(count / orgVehicleAlertsQueryParams.page_size)
                  : 1
              }
              forcePage={orgVehicleAlertsQueryParams.page - 1}
              handlePageClick={(data: TPaginationSelected) => {
                dispatch(
                  setListingQueryParams({
                    ...listingQueryParams,
                    vehicleAlerts: {
                      ...orgVehicleAlertsQueryParams,
                      page: data?.selected + 1,
                    },
                  })
                );
              }}
              paginCurrentPage={orgVehicleAlertsQueryParams.page}
              paginItemsOnCurrentPage={results?.length || 0}
              paginTotalItemsCount={count}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default ScreenVehicleAlerts;
